import React from "react";
import Image from "next/image";
import { Box, CircularProgress, Collapse, IconButton, Tooltip } from "@mui/material";
import { TransitionGroup } from "react-transition-group";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import dayjs from "dayjs";
import { useRecoilValue } from "recoil";
import { dividerState, textColorState, themeState } from "@/lib/store";
import { dateParser, nFormatter } from "@/lib/utils";
import { useNoBackgroundLogos } from "src/hooks/useNoBackgroundLogos";
import styles from "./DataHighlights.module.scss";
import { useDataHighlights } from "./hooks/useDataHighlights";
import { useImageHandler } from "src/hooks/useImageHandler";
import Link from "next/link";
import { DataHighlightsStock } from "@/models/data-highlights";
import { useCompanyLogo, useLogosConfig } from "@/lib/logos";

/**
 * One morning-note highlight's logo. Separate component so `useCompanyLogo` can run per row: it
 * prefers artwork that reads on the page background and plates it white when none exists.
 */
const HighlightLogo = ({
    element,
    plated,
    hidden,
    onMissing,
    onLoaded,
}: {
    element: any;
    plated: boolean;
    hidden: boolean;
    onMissing: () => void;
    onLoaded: (img: HTMLImageElement) => void;
}) => {
    const logosCfg = useLogosConfig();
    const logo = useCompanyLogo(element, "rect", { onMissing });
    const legacyPlate = logosCfg.source === "edge" ? "transparent" : plated ? "#c0c0c0" : "transparent";
    return (
        <Box className={styles.Logo} sx={{ backgroundColor: logo.plate ?? legacyPlate, borderRadius: "4px", minWidth: "80px", minHeight: "35px", height: "min-content" }}>
            {!hidden && !logo.failed && <Image unoptimized src={logo.src} alt={`logo`} layout="fill" objectFit="contain" onError={logo.onError} onLoadingComplete={onLoaded} />}
        </Box>
    );
};

const formatCommentWithDate = (comment: string, parameters: number[]) => {
    let formattedComment = comment;
    let paramIndex = 0;

    formattedComment = formattedComment.replace(/%\$%|\$\%\$/g, () => {
        const param = parameters[paramIndex];
        if (param === undefined) return "";

        let result: string;

        if (parameters.length === 2) {
            result = paramIndex === 0 ? nFormatter(param, 2) : dateParser(dayjs(param), "D MMM YYYY [at] HH:mm");
        } else {
            result = dateParser(dayjs(param), "D MMM YYYY [at] HH:mm");
        }

        paramIndex++;
        return result;
    });

    return formattedComment;
};

export default function DataHighlights({ highlightsData, obfuscated }: { highlightsData: DataHighlightsStock[]; obfuscated?: boolean }) {
    const logosCfg = useLogosConfig(); // re-render when web flips the logo source or rotates the token
    const dividerColor = useRecoilValue(dividerState);
    const textColor = useRecoilValue(textColorState);
    const theme = useRecoilValue(themeState);

    const { data, expand, collapse, expanded, loading } = useDataHighlights(highlightsData);
    const { hasImgError, isPlaceholderImg, handleImgError, handleImgLoad } = useImageHandler("data-highlights");
    const { logoNoBackground } = useNoBackgroundLogos();

    if (!data.length) return null;

    return (
        <div>
            <Box component={TransitionGroup} className={obfuscated ? "obfuscated" : ""} sx={{ display: "flex", flexDirection: "column", mb: 1 }}>
                {data.map((element) => {
                    const stockHighlight = (
                        <>
                            <HighlightLogo
                                element={element}
                                plated={theme === "dark" && !isPlaceholderImg(element.securityId) && !logoNoBackground(element.securityId)}
                                hidden={hasImgError(element.securityId) || isPlaceholderImg(element.securityId)}
                                onMissing={() => handleImgError(element.securityId)}
                                onLoaded={(img) => handleImgLoad(img, element.securityId)}
                            />
                            <Box sx={{ ml: 1 }}>
                                <span style={{ fontWeight: "bold" }}>{element.ticker + " - "}</span>
                                {element.comments
                                    .filter((comment) => comment.text.length)
                                    .slice(0, 2)
                                    .map((comment, index) => (
                                        <span key={index}>{formatCommentWithDate(comment.text, comment.parameters)}. </span>
                                    ))}
                            </Box>
                        </>
                    );

                    return (
                        <Collapse key={element.ticker}>
                            {obfuscated ? (
                                <Box sx={{ display: "flex", cursor: "default", pb: 1 }}>{stockHighlight}</Box>
                            ) : (
                                <Link href={`/s/${element.exchange}/${element.ticker}`} passHref>
                                    <Box sx={{ display: "flex", cursor: "pointer", pb: 1 }}>{stockHighlight}</Box>
                                </Link>
                            )}
                        </Collapse>
                    );
                })}
            </Box>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={20} />
                </Box>
            ) : (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    {!expanded ? (
                        <Tooltip title="More" placement="top" arrow>
                            <IconButton
                                onClick={expand}
                                sx={{
                                    backgroundColor: dividerColor,
                                    "&:hover": {
                                        backgroundColor: "grey",
                                    },
                                }}
                                disabled={obfuscated}
                            >
                                <ExpandMoreIcon sx={{ color: textColor }} />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Less" placement="top" arrow>
                            <IconButton
                                onClick={collapse}
                                sx={{
                                    backgroundColor: dividerColor,
                                    "&:hover": {
                                        backgroundColor: "grey",
                                    },
                                }}
                                disabled={obfuscated}
                            >
                                <ExpandLessIcon sx={{ color: textColor }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            )}
        </div>
    );
}
